import React, { useEffect, useMemo, useRef, useState } from 'react';

import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { dashboardPaymentSlipBatchApis } from '@apis/next/PaymentSlipBatchApis';
import { PaymentSlipSimpleList } from '@components/admin/invoice/PaymentSlipSimpleList';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { TextInput } from '@components/NextForms/TextInput';
import { QueryContainer } from '@components/QueryContainer';
import { paymentSlipBatchStrings } from '@data/paymentSlipBatch';
import { useSearch } from '@hooks/next/useSearch';
import { useFormUtils } from '@hooks/useFormUtils';
import { getNextPageParam } from '@models/next/PaginationType';
import { PaymentSlip, PaymentSlipSearchQuery } from '@models/next/PaymentSlip';
import { PaymentSlipBatch, PaymentSlipBatchSearchType } from '@models/next/PaymentSlipBatch';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { Checklist, useChecklist } from '@utils/useChecklist';
import clsx from 'clsx';
import useIntersection from 'react-use/lib/useIntersection';
import { pick } from 'remeda';

const strings = createStrings({
  ja: {
    selectPaymentSlipItemTitle: '支払伝票を選択',
    noMorePaymentSlips: 'お探しの支払伝票は以上となります',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialog: {
    maxWidth: '960px !important',
  },
  input: {
    width: 0,
  },
  label: {
    minWidth: 'initial',
    width: 50,
    textAlign: 'right',
  },
  paymentSlipListContainer: {
    ...common.middleText,
    height: 300,
    overflow: 'auto',
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  paymentSlipListTable: {
    width: '100%',
    '& th': {
      ...common.fontWeight500,
      lineHeight: '50px',
      textAlign: 'left',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.palette.background.paper,
      zIndex: 10,
    },
    '& td': {
      ...common.textGray700,
      lineHeight: '40px',
      maxWidth: 200,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  actionsContainer: {
    ...common.flexInline,
    flexWrap: 'wrap',
    columnGap: '1.5rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    paddingLeft: 24,
    paddingRight: 24,
    [theme.breakpoints.down('sm')]: {
      columnGap: '8rem',
      rowGap: '0.5rem',
    },
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
  },
  dialogContent: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: 0,
  },

  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 120px 30px) 90px auto',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  headerContainer: {
    paddingLeft: 24,
    paddingRight: 24,
  },
}));

interface PaymentSlipListProps {
  items: PaymentSlip[];
  checklist: Checklist<number>;
  isEnd?: boolean;
  onScrollEnd?: () => void;
}

const PaymentSlipList: React.FC<PaymentSlipListProps> = (props) => {
  const { items, checklist, isEnd, onScrollEnd } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const containerRef = useRef<HTMLDivElement | null>(null);

  const tailRef = useRef<HTMLDivElement | null>(null);

  const intersection = useIntersection(tailRef, {
    root: containerRef.current,
  });

  useEffect(() => {
    intersection?.isIntersecting && onScrollEnd?.();
  }, [intersection?.isIntersecting]);

  return (
    <div ref={containerRef} className={classes.paymentSlipListContainer}>
      <PaymentSlipSimpleList items={items} checklist={checklist} />

      {isEnd && (
        <div
          className={clsx(classes.textAlignCenter, classes.textGray400, classes.smallerText)}
          style={{ marginBottom: '1rem' }}
        >
          {strings.noMorePaymentSlips}
        </div>
      )}

      <div ref={tailRef} style={{ height: 1 }} />
    </div>
  );
};

export interface PaymentSlipAddDialogProps {
  open: boolean;
  onClose: () => void;
  initialQuery: PaymentSlipSearchQuery;
  paymentSlipBatch?: PaymentSlipBatch;
}

export const PaymentSlipAddDialog: React.FC<PaymentSlipAddDialogProps> = (props) => {
  const { open, onClose, initialQuery, paymentSlipBatch } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();

  const { search } = useSearch(PaymentSlipBatchSearchType);

  const [query, setQuery] = useState<PaymentSlipSearchQuery>(initialQuery ?? {});

  const paymentSlipsQuery = dashboardPaymentSlipApis.useInfiniteQuery(
    '/dashboard/payment-slip',
    {
      queries: { ...query },
    },
    {
      getPageParamList: () => ['page'],
      getNextPageParam,
    },
  );

  const paymentSlips = useMemo(
    () => paymentSlipsQuery.data?.pages.map((p) => p.results).flat() ?? [],
    [paymentSlipsQuery.data],
  );

  const checklist = useChecklist([], paymentSlips.map((d) => d.id) ?? []);

  const patchPaymentSlipBatch = dashboardPaymentSlipBatchApis.usePatch(
    '/dashboard/payment-slip-batch/:id',
    {
      params: { id: search.id },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardPaymentSlipBatchApis.getKeyByPath('get', '/dashboard/payment-slip-batch/:id', {
            params: { id: search.id },
          }),
        );
        queryClient.invalidateQueries(
          dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
        );
        checklist.setChecklist([]);
        onClose();
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [patchPaymentSlipBatch],
  });

  const form = useForm(PaymentSlipSearchQuery, {
    initialOutput: query,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, ['d2d_no', 'payment_slip_no', 'payee', 'payment_type', 'job_code']),
        form,
      ),
    [fields],
  );

  const renderSearchForm = () => {
    return (
      <form
        {...form.bindForm({
          onSubmit(d) {
            setQuery({ ...d, page: 1 });
          },
        })}
        className={classes.tagSearchInputContainer}
      >
        <TagSearchInput
          controller={controller}
          placeholder={paymentSlipBatchStrings.tagSearchInputPlaceholder}
          styles={{
            wrapper: {
              gridColumn: '1 / -1',
            },
          }}
        />

        <TextInput
          field={fields.payment_date_after}
          inputProps={{ placeholder: getFieldLabel(fields.payment_date_after) }}
        />

        <label className={classes.searchTailLabel}>{formStrings.from}</label>

        <TextInput
          field={fields.payment_date_before}
          inputProps={{ placeholder: getFieldLabel(fields.payment_date_before) }}
        />

        <label className={classes.searchTailLabel}>{formStrings.until}</label>

        <TextInput
          field={fields.cutoff_date_after}
          inputProps={{ placeholder: getFieldLabel(fields.cutoff_date_after) }}
        />

        <label className={classes.searchTailLabel}>{formStrings.from}</label>

        <TextInput
          field={fields.cutoff_date_before}
          inputProps={{ placeholder: getFieldLabel(fields.cutoff_date_before) }}
        />

        <label className={classes.searchTailLabel}>{formStrings.until}</label>

        <Button
          text={formStrings.filter}
          color="primary"
          type="submit"
          size="small"
          shape="round"
        />
      </form>
    );
  };

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialog,
      }}
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      <ActionableDialogTitle>{strings.selectPaymentSlipItemTitle}</ActionableDialogTitle>
      <ActionableDialogContent classes={{ root: classes.dialogContent }}>
        <div className={classes.headerContainer}>
          {renderAlertsAndSpinnerModal()}
          {renderSearchForm()}
        </div>

        <QueryContainer style={{ height: 300 }} queries={[paymentSlipsQuery]}>
          <PaymentSlipList
            items={paymentSlips}
            checklist={checklist}
            onScrollEnd={() => paymentSlipsQuery.fetchNextPage()}
            isEnd={paymentSlips.length >= (paymentSlipsQuery.data?.pages[0]?.count ?? Infinity)}
          />
        </QueryContainer>
      </ActionableDialogContent>

      <ActionableDialogContentDivider />

      <div
        className={clsx(classes.flexInline, classes.itemsCenter, classes.justifyEnd)}
        style={{ padding: '1rem' }}
      >
        <Button
          color="primary"
          text={formStrings.ok}
          size="small"
          disabled={checklist.checklist.length === 0}
          onClick={() => {
            patchPaymentSlipBatch.mutateAsync({
              payment_slip_ids: [
                ...(paymentSlipBatch?.payment_slips.map((ps) => ps.id) ?? []),
                ...checklist.checklist,
              ],
            });
          }}
        />
      </div>
    </ActionableDialog>
  );
};
